import React from "react";
import PropTypes from "prop-types";
import {
  Card,
  CardContent,
  Box,
  Typography,
  Tooltip,
  Skeleton,
  Stack,
} from "@mui/material";
import { InfoOutlined as InfoIcon } from "@mui/icons-material";
import MiniAreaChart from "./MiniAreaChart";
import { colors, customShadows, borderRadius, borderRadiusPx } from "../../../../theme";

/**
 * MetricCard - Top KPI card displaying metric label, value, trend badge, and optional mini sparkline.
 */
const MetricCard = ({
  title,
  value,
  prefix = "",
  suffix = "",
  trend = null,
  tooltip = "",
  valueColor,
  chartColor = colors.charts.blue,
  chartData = null,
  loading = false,
  sx = {},
}) => {
  const isNegativeVal = valueColor === "error" || valueColor === colors.error.main;
  const defaultValColor = isNegativeVal
    ? colors.error.main
    : valueColor || colors.dashboard.value;
    
  return (
    <Card
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        borderRadius: borderRadiusPx.card,
        border: `1px solid ${colors.dashboard.cardBorder}`,
        boxShadow: customShadows.card,
        backgroundColor: colors.dashboard.cardBg,
        transition: "all 0.25s ease-in-out",
        "&:hover": {
          transform: "translateY(-3px)",
          boxShadow: customShadows.cardHover,
          borderColor: chartColor,
        },
        ...sx,
      }}
    >
      <CardContent sx={{ p: "20px 20px 12px 20px !important" }}>
        {/* Header: Title + Info Tooltip */}
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.25}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              fontSize: "0.84rem",
              color: colors.slate[600],
              letterSpacing: "-0.01em",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {title}
          </Typography>

          {tooltip && (
            <Tooltip title={tooltip} arrow placement="top">
              <InfoIcon
                sx={{
                  fontSize: "0.95rem",
                  color: colors.slate[400],
                  cursor: "pointer",
                  transition: "color 0.2s",
                  "&:hover": { color: colors.slate[600] },
                }}
              />
            </Tooltip>
          )}
        </Stack>

        {/* Value Display */}
        {loading ? (
          <Skeleton variant="text" width="65%" height={40} sx={{ my: 0.5 }} />
        ) : (
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              fontSize: { xs: "1.45rem", sm: "1.6rem" },
              color: defaultValColor,
              letterSpacing: "-0.03em",
              lineHeight: 1.2,
              mb: 1,
            }}
          >
            {prefix}
            {value}
            {suffix}
          </Typography>
        )}
      </CardContent>

      {/* Optional Bottom Sparkline Area Chart */}
      {chartData && chartData.length > 0 && (
        <Box sx={{ px: 1.5, pb: 1, mt: "auto" }}>
          <MiniAreaChart data={chartData} color={chartColor} height={42} />
        </Box>
      )}
    </Card>
  );
};

MetricCard.propTypes = {
  title: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  prefix: PropTypes.string,
  suffix: PropTypes.string,
  trend: PropTypes.shape({
    value: PropTypes.string,
    percentage: PropTypes.number,
    isPositive: PropTypes.bool,
    label: PropTypes.string,
  }),
  tooltip: PropTypes.string,
  valueColor: PropTypes.string,
  chartColor: PropTypes.string,
  chartData: PropTypes.arrayOf(PropTypes.number),
  loading: PropTypes.bool,
  sx: PropTypes.object,
};

export default MetricCard;
